/**
 * BulkEmailStatsCards — E-93 Story 93.3.
 *
 * Four KPI cards shown at the top of BulkEmailPage. The grid is 2×2 on
 * mobile, 4-column on desktop. Numbers come from `useBulkEmailStats`
 * (tenant-scoped, 30-day window by default).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBulkEmailStats } from '@/hooks/use-bulk-email-stats';
import { useTranslation } from '@kgc/i18n';
import type { JSX } from 'react';

export interface BulkEmailStatsCardsProps {
  windowDays?: number;
}

export function BulkEmailStatsCards({ windowDays = 30 }: BulkEmailStatsCardsProps): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error } = useBulkEmailStats(windowDays);

  // Early states render the same 4-card skeleton so the page layout
  // doesn't jump when the query resolves.
  const placeholder = isLoading || error || !data;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="bulk-email-stats-cards">
      <StatCard
        title={t('email.bulkSend.stats.totalBatches')}
        value={placeholder ? '—' : data.totalBatches.toString()}
        hint={t('email.bulkSend.stats.windowHint', { days: windowDays })}
      />
      <StatCard
        title={t('email.bulkSend.stats.activeCampaigns')}
        value={placeholder ? '—' : data.activeCampaigns.toString()}
        hint={t('email.bulkSend.stats.activeHint')}
        valueClassName="text-blue-700 dark:text-blue-200"
      />
      <StatCard
        title={t('email.bulkSend.stats.completedToday')}
        value={placeholder ? '—' : data.completedToday.toString()}
        hint={t('email.bulkSend.stats.completedHint')}
        valueClassName="text-emerald-700 dark:text-emerald-200"
      />
      <StatCard
        title={t('email.bulkSend.stats.successRate')}
        value={placeholder || data.successRate === null ? '—' : formatPercent(data.successRate)}
        hint={t('email.bulkSend.stats.successHint', {
          recipients: placeholder ? 0 : data.totalRecipientsWindow,
        })}
      />
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  hint: string;
  valueClassName?: string;
}

function StatCard({ title, value, hint, valueClassName }: StatCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-2xl font-bold ${valueClassName ?? ''}`}>{value}</p>
        <p className="pt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

/**
 * Formats a 0..1 ratio as a locale-aware percentage with 1 decimal.
 * Exported for the unit test so we pin the rounding behavior
 * (0.9755 → "97.6%" not "97.5%").
 */
export function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}
